import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import PostCard from '../../components/postcard/PostCard';
import Sidebar from '../../components/sidebar/Sidebar';
import Header from '../../components/Layout/Header/Header';
import SearchPopup from '../../components/SearchPopUp/SearchPopup';
import { postsService, usersService, getMediaBaseUrl, Post } from '../../api/api.service';
import '../../components/Layout/Header/Header.css';
import '../../styles/global.css';

interface SearchResultsPageProps {
  onSearch: (query: string) => void;
}

const SearchResultsPage: React.FC<SearchResultsPageProps> = ({ onSearch }) => {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [authors, setAuthors] = useState<Record<number, { name: string; avatar: string }>>({});
  const [loading, setLoading] = useState(true);
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);
  
  const query = searchParams.get('s') || '';
  const mediaBaseUrl = getMediaBaseUrl();
  const fallbackAvatar = `${mediaBaseUrl}/wp-content/uploads/2025/10/profile.jpg`;

  useEffect(() => {
    const fetchSearchResults = async (searchQuery: string) => {
      setLoading(true);
      try {
        // Search for posts using WordPress REST API
        const postsData = await postsService.searchPosts(searchQuery, 20);
        setPosts(postsData);

      // Get unique author IDs
      const authorIds = Array.from(new Set(postsData.map((p) => p.author)));
      
      // Fetch authors
      const authorResults = await usersService.getUsers(authorIds);
        
        const authorsMap: Record<number, { name: string; avatar: string }> = {};
        authorResults.forEach((author) => {
          authorsMap[author.id] = {
            name: author.name,
            avatar: author.avatar_urls?.['96'] || author.avatar_urls?.['48'] || fallbackAvatar
          };
        });
        setAuthors(authorsMap);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    if (query) {
      fetchSearchResults(query);
    }
  }, [query, fallbackAvatar]);

  const handleSearch = (searchQuery: string) => {
    onSearch(searchQuery);
  };

  const handleSearchAgain = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const newQuery = formData.get('search') as string;
    if (newQuery.trim()) {
      onSearch(newQuery.trim());
    }
  };

  return (
    <>
      <Header onSearchClick={() => setIsSearchPopupOpen(true)} />
      <SearchPopup 
        isOpen={isSearchPopupOpen} 
        onClose={() => setIsSearchPopupOpen(false)}
        onSearch={handleSearch}
      />
      
      <main style={{ maxWidth: '1180px', margin: '0 auto', padding: '0 20px', marginTop: '120px' }}>
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            color: '#1e40af', 
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            Search result for: {query}
          </h1>
          
          <nav style={{ textAlign: 'center', marginBottom: '40px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none' }}>Lifestyle Online Sites</Link>
            <span style={{ color: '#6b7280', margin: '0 8px' }}>/</span>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none' }}>Lifestyle Online</Link>
            <span style={{ color: '#6b7280', margin: '0 8px' }}>/</span>
            <span style={{ color: '#6b7280' }}>Search results for '{query}'</span>
          </nav>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '30px' }}>
          <div style={{ flex: '2.75' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '18px', color: '#6b7280' }}>Searching...</div>
              </div>
            ) : posts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                {posts.map((post) => {
                  const postImage = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || 
                    `${mediaBaseUrl}/wp-content/uploads/2025/10/placeholder.jpg`;
                  
                  return (
                    <PostCard
                      key={post.id}
                      id={post.id}
                      image={postImage}
                      title={post.title.rendered}
                      author={authors[post.author]?.name || 'Unknown'}
                      authorImage={authors[post.author]?.avatar}
                      date={new Date(post.date).toDateString()}
                      excerpt={post.excerpt?.rendered.replace(/<\/?[^>]+(>|$)/g, '') || ''}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <h2 style={{ 
                  fontSize: '28px', 
                  fontWeight: '700', 
                  color: '#1e40af', 
                  marginBottom: '16px' 
                }}>
                  Nothing found
                </h2>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#6b7280', 
                  marginBottom: '24px',
                  lineHeight: '1.6'
                }}>
                  Sorry, but nothing matched your search terms. Please try again with some different keywords.
                </p>
                <form onSubmit={handleSearchAgain} style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <input
                    type="text"
                    name="search"
                    defaultValue={query}
                    placeholder="Search again..."
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      width: '300px',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="submit"
                    style={{
                      background: 'linear-gradient(135deg, #ff6b70 0%, #ff879e 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    Search
                  </button>
                </form>
              </div>
            )}
          </div>

          <aside style={{ flex: '1.25', marginTop: 0 }}>
            <Sidebar />
          </aside>
        </div>
      </main>
    </>
  );
};

export default SearchResultsPage;

